<template>
  <div v-if="showUploadModal" class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40">
    <div class="p-6 glass-card mb-2 w-[80%] max-w-md min-h-[35%] shadow-xl z-50 flex flex-col justify-between">
      <h2 class="text-xl font-bold mb-4">Upload Firmware</h2>
      
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">Version</label>
          <input
            v-model="newVersion"
            type="text"
            placeholder="e.g., 1.4.13"
            class="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium mb-2">Device Type</label>
          <GlassSelect
            v-model="newDeviceType"
            :options="['esp32-p4', 'esp32-s3', 'esp32-c6']"
            placeholder="Select device type"
          />
        </div>

        <div>
          <label class="block text-sm font-medium mb-2">Firmware File</label>
          <div class="border-2 border-dashed border-gray-600 rounded-md p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
            <input
              type="file"
              @change="handleFileUpload"
              accept=".bin"
              class="hidden"
              id="firmware-file"
            />
            <label for="firmware-file" class="cursor-pointer">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mx-auto mb-2 text-gray-400">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <p class="text-gray-400 text-sm">
                {{ selectedFile ? selectedFile.name : 'Click to upload or drag and drop' }}
              </p>
              <p class="text-gray-500 text-xs mt-1">.bin files only</p>
            </label>
          </div>
        </div>
      </div>

      <div class="flex gap-3 mt-6">
        <button
          @click="uploadFirmwareRelease"
          :disabled="!canUpload"
          :class="[
            'flex-1 px-4 py-2 rounded-md transition-all',
            canUpload
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          ]"
        >
          Upload
        </button>
        <button
          @click="closeUploadModal"
          class="flex-1 px-4 py-2 bg-gray-600 rounded-md hover:bg-gray-700 transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>


  <div class="p-6 dark:text-white glass-card-lite">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <div class="flex items-center gap-3">
        <div class="bg-blue-500/20 p-2 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-blue-500">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 16v-4"></path>
            <path d="M12 8h.01"></path>
          </svg>
        </div>
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Firmware Management (OTA)
        </h1>
      </div>

      <button
        @click="showUploadModal = true"
        class="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="17 8 12 3 7 8"></polyline>
          <line x1="12" y1="3" x2="12" y2="15"></line>
        </svg>
        <span>Upload Firmware</span>
      </button>
    </div>
    

    <!-- Current Version Card -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      <div class="glass-card-lite p-8">
        <p class="text-gray-400 text-sm mb-2">CURRENT VERSION</p>
        <p class="text-5xl font-bold text-blue-500">{{ currentVersion }}</p>
      </div>

      <div class="glass-card-lite p-6">
        <p class="text-gray-400 text-sm mb-3">API ENDPOINT</p>
        <div class="rounded-md p-3 border border-gray-700/50">
          <code class="dark:text-cyan-400 text-cyan-800  text-sm font-mono break-all">
            {{ apiEndpoint }}
          </code>
        </div>
      </div>
    </div>
    

    <!-- Firmware Releases Table -->
    <div class="mb-6">
      <h2 class="text-xl font-semibold text-white mb-4">Firmware Releases</h2>
      
      <GenericMivuLiteTable
        :columns="columns"
        :items="firmwareReleases"
        itemKey="id"
        emptyMessage="No firmware releases found. Upload firmware to get started."
      >
        <template #device_type="{ value }">
          <span class="dark:text-gray-300 font-semibold">{{ value }}</span>
        </template>

        <template #version="{ value, item  }">
          <div class="space-x-2">
            <span class="text-blue-400 font-semibold">{{ value }}</span>
            <span v-if="item.is_latest" class="bg-blue-700 py-1 px-2 rounded-lg text-xs text-white font-bold">Latest</span>
          </div>
        </template>

        <template #file_size="{ value }">
          <span class="dark:text-gray-300">{{ formatSize(value) }}</span>
        </template>

        <template #created_at="{ value }">
          {{ formatValue("created_at", value) }}
        </template>

        <template #force_update="{ value }">
        <span
          :class="[
            'px-3 py-2 rounded-full text-xs font-medium',
            value ? 'bg-yellow-500/20 text-yellow-500' : 'bg-gray-500/20 text-gray-500'
          ]"
        >
          {{ value ? 'Force Update' : 'Optional' }}
        </span>
      </template>

        <template #actions="{ item }">
          <div class="flex gap-2">
            <button
              v-if="item.status !== 'Active'"
              @click="setActiveVersion(item)"
              class="btn flex items-center gap-2 btn bg-green-600 hover:bg-green-700 transition-all text-sm"
              title="Set Active"
            >
              <LucideActivity/>
              Activate
            </button>
            <button
              @click="deleteFirmware(item.id)"
              class="btn flex items-center gap-2 btn bg-red-600 hover:bg-red-700 transition-all text-sm"
              title="Delete"
            >
              <Trash2/>
              Delete
            </button>
          </div>
        </template>
      </GenericMivuLiteTable>
    </div>

    <!-- Success Toast -->
    <div
      v-if="showSuccessToast"
      class="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import GenericMivuLiteTable from '@/components/GenericMivuLiteTable.vue';
import GlassSelect from '@/components/GlassSelect.vue';
import {
  getFirmwareVersion,
  getFirmwareReleases,
  getFirmwareHistory,
  updateFirmwareRelease,
  deleteFirmwareRelease,
  uploadFirmware,
} from '@/services/mivulite/firmwareService';
import { Trash2, LucideActivity } from 'lucide-vue-next';

const columns = [
  { key: 'version', label: 'Version' },
  { key: 'device_type', label: 'Device Type' },
  { key: 'file_size', label: 'Size' },
  { key: 'uploaded_by', label: 'Uploaded' },
  { key: 'force_update', label: 'Status' },
  { key: 'created_at', label: 'Uploaded' },
  { key: 'actions', label: 'Actions' }
];

const firmwareVersion = ref({});
const firmwareReleases = ref([])
const currentVersion = ref('');
const apiEndpoint = ref('');
const showUploadModal = ref(false);
const showSuccessToast = ref(false);
const successMessage = ref('');

const newVersion = ref('');
const newDeviceType = ref('');
const selectedFile = ref(null);

const canUpload = computed(() => {
  return newVersion.value && newDeviceType.value && selectedFile.value;
});

const fetchFirmwareVersion = async () => {
  try {
    firmwareVersion.value = await getFirmwareVersion();
    currentVersion.value = firmwareVersion.value.version;
    apiEndpoint.value = firmwareVersion.value.url
  } catch (error) {
    console.error('Error fetching firmware releases:', error);
  }
};


const fetchFirmwareReleases = async () => {
  try {
    const response = await getFirmwareReleases();
    firmwareReleases.value = response['releases']


  } catch (error) {
    console.error('Error fetching firmware releases:', error);
  }
};

const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file && file.name.endsWith('.bin')) {
    selectedFile.value = file;
  } else {
    alert('Please select a .bin file');
  }
};

const uploadFirmwareRelease = async () => {
  if (!canUpload.value) return;

  try {
    await uploadFirmware({
      version: newVersion.value,
      device_type: newDeviceType.value,
      file: selectedFile.value
    });
    await fetchFirmwareReleases();
    closeUploadModal();
    showToast('Firmware uploaded successfully!');
  } catch (error) {
    console.error('Error uploading firmware:', error);
    alert('Error uploading firmware. Please try again.');
  }
};

const closeUploadModal = () => {
  showUploadModal.value = false;
  newVersion.value = '';
  newDeviceType.value = '';
  selectedFile.value = null;
};

const downloadFirmware = (item) => {
  const link = this.$refs.downloadLink;
  link.href = this.orgInstaller.link;
  link.download = '';
  link.click();
  showToast(`Downloading ${item.version}...`);
};

const setActiveVersion = async (item) => {
  try {
    await setActiveFirmware(item.id);
    await fetchFirmwareVersion();
    const current = await getCurrentVersion();
    currentVersion.value = current.version;
    showToast(`Version ${item.version} set as active!`);
  } catch (error) {
    console.error('Error setting active firmware:', error);
  }
};

const deleteFirmware = async (id) => {
    try {
      await deleteFirmwareRelease(id);
      await fetchFirmwareReleases();
      showToast('Firmware deleted successfully!');
    } catch (error) {
      console.error('Error deleting firmware:', error);
    }
};

const formatSize = (bytes) => {
  if (!bytes) return 'N/A';
  const mb = (bytes / (1024 * 1024)).toFixed(2);
  return `${mb} MB`;
};

const showToast = (message) => {
  successMessage.value = message;
  showSuccessToast.value = true;
  setTimeout(() => {
    showSuccessToast.value = false;
  }, 3000);
};


const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  };
  return new Date(dateString).toLocaleString(undefined, options);
};


const formatValue = (key, value) => {
  if (key === "last_seen" || key === "created_at") {
    return formatDateTime(value);
  }
  return value !== null && value !== undefined ? value : "N/A";
};

onMounted(() => {
  fetchFirmwareVersion();
  fetchFirmwareReleases();
});
</script>
